import React, {useContext, useEffect, useState} from 'react';
import {useWindowDimensions, View, Image} from 'react-native';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import MainStyle from '../../../../../../MainStyle';
import {isFullSwing, tabletBreakpoint} from "../../../../../constants";
import Input from "../../../../../components/Input";
import Button from "../../../../../components/Button";
import visa from "../../../../../assets/img/icons/visa.png";
import SavedCards from "../../../../../components/SavedCards";
import ApplePayButton from "../../../../../components/ApplePayButton";
import KycTerm from "../../../../Auth/SignUp/steps/KycTerm";
import {StoreContext} from "../../../../../components/StoreContextProvider";
import {colors} from "../../../../../assets/styles/colors";
import Div from "../../../../../lib/Div";
import P from "../../../../../lib/P";
import LoadingKYC from "../../../../../components/LoadingKYC";
import FullSwingDeposit from "../../../../../components/FullSwingDeposit";
import {getFieldError} from "../../../../../utils";
import CompleteKYC from "../../../../../modals/CompleteKYC";
import AddCardInfoModal from "./AddCardInfoModal";
import {isCardTokenizationSupported} from "../../../../../services/cardTokenization";
import {depositAmountPattern, parseDepositAmountCents} from "./depositAmount";

const positiveDepositAmountMessage = 'Enter an amount greater than $0.';

const Wallet = () => {
  const Context = useContext(StoreContext);
  const {user, cancelModalConfig} = Context;
  const {width} = useWindowDimensions();
  const isSm = width < tabletBreakpoint;
  const formik = useFormik({
    initialValues: {amount: '50'},
    validationSchema: Yup.object({
      amount: Yup.string().required()
        .matches(depositAmountPattern, 'Invalid Value')
        .test('positive-amount', positiveDepositAmountMessage, value => {
          if (!value || !depositAmountPattern.test(value)) return true;
          return parseDepositAmountCents(value) !== null;
        })
    }),
    onSubmit: () => {
      console.log('onSubmit');
    }
  });
  const [showOnce, setShowOnce] = useState(false);
  const kycStatus = user?.kycStatus;
  // const kycStatus = 1 as any as number;
  const [showWarning, setShowWarning] = useState(false);
  const [addCardDepositAmountCents, setAddCardDepositAmountCents] = useState<number | null>(null);
  const canAddCard = isCardTokenizationSupported();
  const amountCents = parseDepositAmountCents(formik.values.amount);

  const showDepositAmountError = () => {
    void formik.setFieldTouched('amount', true);
  };

  const restartKyc = () => {
    if (!user) return;
    Context.setUser({...user, kycStatus: 0});
  };

  const getUser = () => {
    setTimeout(() => {
      Context.getUser().catch(err => {
        console.log('err', err);
      });
    }, 300);
  };

  const openAddCardInfoModal = () => {
    if (!canAddCard) return;

    if (amountCents === null) {
      showDepositAmountError();
      return;
    }

    setAddCardDepositAmountCents(amountCents);
  };

  useEffect(() => {
    if ((kycStatus === 0 || kycStatus === 2) && !showOnce) {
      setShowOnce(true);

      if (isFullSwing) {
        setShowWarning(true);

      } else {
        Context.setCancelModalConfig({
          visible: true, title: 'Complete KYC', titleColor: MainStyle.c_yellow, rightBtnText: 'COMPLETE KYC',
          subtitle: 'We will need to verify your identity to withdraw or deposit.',
          onClose: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false}),
          onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
        });
      }
    }
  }, [kycStatus, showOnce]);

  return (
    <Div w={isFullSwing ? '100%' : 367} mx="auto" flex={1}>
      {kycStatus === 0 || kycStatus === 2 ? (
        <KycTerm redirectUser={getUser}/>
      ) : kycStatus === 3 || kycStatus === 4 ? (
        <LoadingKYC kycStatus={kycStatus} restartKyc={restartKyc}/>
      ) : (
        <Div column justifyContent="center" alignItems="center" flex={1} pt={isFullSwing ? 30 : 15}>
          {isFullSwing ? (
            <FullSwingDeposit
              formik={formik} canAddCard={canAddCard} amountCents={amountCents} onInvalidAmount={showDepositAmountError}
            />
          ) : (
            <>
              <Div w={330} mb={45} pt={23} rounded={20} bg={colors.new_input_color} borderWidth={1}
                   borderColor={colors.settings_border}>
                <P ta="center" fs={18} fw="500" mb={5}>AMOUNT IN $</P>

                <Input
                  type="numeric" placeholder="AMOUNT" dollarMask useGrouping w={240} h={60} mx="auto" rounded={20}
                  borderColor={colors.settings_border} value={formik.values.amount} fs={32}
                  onChangeText={formik.handleChange('amount')} textAlign="center" onBlur={formik.handleBlur('amount')}
                  error={getFieldError('amount', formik)} color={colors.newGreenText} mb={34}
                />

                {canAddCard ? (
                  <Button text="ENTER CARD INFO" w={240} mb={37} mx="auto" onPress={openAddCardInfoModal}/>
                ) : (
                  <P ta="center" fs={14} lh={20} color={colors.gray} w={240} mb={37} mx="auto">
                    New debit cards can only be added in the Evenplay web app. Saved cards remain available here.
                  </P>
                )}
              </Div>

              <View>
                <ApplePayButton isSm={isSm} amountCents={amountCents} onInvalidAmount={showDepositAmountError}/>

                <SavedCards amountCents={amountCents} onInvalidAmount={showDepositAmountError}/>

                <Div h={15}/>

                <Image
                  source={visa}
                  style={[MainStyle.w_170, MainStyle.h_51, MainStyle.rm_contain, MainStyle.m_x_auto, MainStyle.m_b_20]}
                />
              </View>
            </>
          )}
        </Div>
      )}

      <CompleteKYC visible={showWarning} onClose={() => setShowWarning(false)}/>
      {addCardDepositAmountCents !== null && (
        <AddCardInfoModal
          visible amountCents={addCardDepositAmountCents} onClose={() => setAddCardDepositAmountCents(null)}
        />
      )}
    </Div>
  );
};

export default React.memo(Wallet);
